/**
 * Shapefile Export Module
 * Handles exporting GeoJSON to Shapefile format
 */

import shpwrite from '@mapbox/shp-write';
import proj4 from 'proj4';

/**
 * WKT definitions for common projections
 */
const PROJECTION_WKT = {
    'EPSG:4326': 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]]',
    'EPSG:3857': 'PROJCS["WGS 84 / Pseudo-Mercator",GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]],PROJECTION["Mercator_1SP"],PARAMETER["central_meridian",0],PARAMETER["scale_factor",1],PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],EXTENSION["PROJ4","+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs"],AUTHORITY["EPSG","3857"]]'
};

/**
 * Convert EPSG code to WKT, or return WKT as-is
 * @param {string} projection - EPSG code or WKT string
 * @returns {string} Full WKT string
 */
function getProjectionWKT(projection) {
    // If it's an EPSG code we have a definition for, use it
    if (PROJECTION_WKT[projection]) {
        return PROJECTION_WKT[projection];
    }
    // Otherwise assume it's already WKT and return as-is
    return projection;
}

/**
 * Read feature count from shapefile binary
 * @param {ArrayBuffer} shpData - Shapefile binary data
 * @returns {number} Number of features in the shapefile
 */
function readShapefileFeatureCount(shpData) {
    try {
        // Shapefile format:
        // Header is 100 bytes
        // Bytes 0-3: File Code (9994) - big endian
        // Bytes 24-27: File Length (in 16-bit words) - big endian
        // Bytes 32-35: Shape Type - little endian

        const view = new DataView(shpData);

        // Get file length in 16-bit words (big endian)
        const fileLengthWords = view.getInt32(24, false); // false = big endian
        const fileLengthBytes = fileLengthWords * 2;

        // Count records by parsing the file
        let featureCount = 0;
        let offset = 100; // Start after header

        while (offset < fileLengthBytes) {
            if (offset + 8 > fileLengthBytes) break;

            // Record header:
            // Bytes 0-3: Record Number - big endian
            // Bytes 4-7: Content Length (in 16-bit words) - big endian
            const contentLengthWords = view.getInt32(offset + 4, false);
            const contentLengthBytes = contentLengthWords * 2;

            featureCount++;

            // Move to next record (8 byte header + content)
            offset += 8 + contentLengthBytes;
        }

        return featureCount;
    } catch (error) {
        console.error('Error reading shapefile feature count:', error);
        return -1;
    }
}


/**
 * Transform geometry coordinates
 * @param {Object} geometry - GeoJSON geometry
 * @param {Function} transform - proj4 transform function
 */
function transformGeometry(geometry, transform) {
    if (!geometry || !geometry.type) return;

    switch (geometry.type) {
        case 'Point':
            geometry.coordinates = transform.forward(geometry.coordinates);
            break;
        case 'MultiPoint':
        case 'LineString':
            geometry.coordinates = geometry.coordinates.map(coord => transform.forward(coord));
            break;
        case 'MultiLineString':
        case 'Polygon':
            geometry.coordinates = geometry.coordinates.map(ring =>
                ring.map(coord => transform.forward(coord))
            );
            break;
        case 'MultiPolygon':
            geometry.coordinates = geometry.coordinates.map(polygon =>
                polygon.map(ring =>
                    ring.map(coord => transform.forward(coord))
                )
            );
            break;
        case 'GeometryCollection':
            geometry.geometries.forEach(geom => transformGeometry(geom, transform));
            break;
    }
}

/**
 * Export a GeoJSON layer as a shapefile (.zip)
 * @param {Object} geojson - The GeoJSON data
 * @param {string} fileName - The name for the output file (without extension)
 * @param {Object} metadata - Optional layer metadata containing projection info
 * @param {string} projection - Target projection (EPSG code or WKT)
 * @param {string} projectionName - Friendly name for target projection
 */
export async function exportAsShapefile(geojson, fileName, metadata = null, projection = null, projectionName = null) {
    try {
        console.log('Exporting shapefile:', fileName, '(' + (geojson?.features?.length || 0) + ' features)');

        // Define common projections
        proj4.defs('EPSG:3857', '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs');

        // Reproject data if needed
        let exportData = geojson;
        if (projection && projection !== 'EPSG:4326') {
            console.log(`Reprojecting to ${projectionName}`);

            // Determine target projection
            let targetProj;
            if (projection.startsWith('EPSG:')) {
                targetProj = projection;
            } else {
                // It's WKT, define it
                proj4.defs('EXPORT_TARGET', projection);
                targetProj = 'EXPORT_TARGET';
            }

            // Create transformation
            const transform = proj4('EPSG:4326', targetProj);

            // Clone and reproject
            exportData = JSON.parse(JSON.stringify(geojson));
            exportData.features.forEach(feature => {
                if (feature.geometry) {
                    transformGeometry(feature.geometry, transform);
                }
            });
        }

        // Remove internal properties and filter out features with null geometries
        const featuresWithoutNull = exportData.features.filter(feature => {
            const hasGeometry = feature.geometry !== null && feature.geometry !== undefined;
            if (!hasGeometry) {
                console.warn('Filtering out feature with null geometry');
            }
            return hasGeometry;
        });

        const cleanedGeoJSON = {
            type: 'FeatureCollection',
            features: featuresWithoutNull.map((feature, index) => {
                // Create a completely clean geometry object
                let geometry;

                if (feature.geometry.type === 'Polygon') {
                    // Normalize Polygons to MultiPolygons to work around shp-write bug
                    geometry = {
                        type: 'MultiPolygon',
                        coordinates: JSON.parse(JSON.stringify([feature.geometry.coordinates]))
                    };
                } else if (feature.geometry.type === 'LineString') {
                    // Create a completely fresh LineString object (shp-write has bugs with LineStrings)
                    geometry = {
                        type: 'LineString',
                        coordinates: JSON.parse(JSON.stringify(feature.geometry.coordinates))
                    };
                } else {
                    // For other types, deep clone to ensure no references
                    geometry = JSON.parse(JSON.stringify(feature.geometry));
                }

                // Ensure at least one property exists (shp-write may fail with empty properties)
                const properties = Object.keys(feature.properties || {})
                    .filter(key => !key.startsWith('__'))
                    .reduce((obj, key) => {
                        const value = feature.properties[key];
                        // Skip empty strings and null/undefined values - shp-write has issues with them
                        if (value !== '' && value !== null && value !== undefined) {
                            obj[key] = value;
                        }
                        return obj;
                    }, {});

                // Add unique FID for each feature (critical for shp-write)
                properties.FID = index;

                // Create a completely fresh feature object
                return {
                    type: 'Feature',
                    geometry: geometry,
                    properties: properties
                };
            })
        };

        const cleanFileName = fileName.replace(/\.zip$/i, '');

        // Validate geometries before export
        const invalidFeatures = [];
        cleanedGeoJSON.features.forEach((feature, index) => {
            if (!feature.geometry || !feature.geometry.type) {
                invalidFeatures.push({ index, reason: 'Missing geometry or geometry type', feature });
            } else if (feature.geometry.type === 'Point') {
                const coords = feature.geometry.coordinates;
                if (!coords || !Array.isArray(coords) || coords.length < 2) {
                    invalidFeatures.push({ index, reason: 'Invalid Point coordinates', feature });
                } else if (!isFinite(coords[0]) || !isFinite(coords[1])) {
                    invalidFeatures.push({ index, reason: 'Point has NaN/Infinity coordinates', feature });
                }
            } else if (feature.geometry.type === 'MultiPoint') {
                const coords = feature.geometry.coordinates;
                if (!coords || !Array.isArray(coords) || coords.length === 0) {
                    invalidFeatures.push({ index, reason: 'Empty MultiPoint coordinates', feature });
                } else {
                    const hasInvalidCoords = coords.some(coord =>
                        !coord || !Array.isArray(coord) || coord.length < 2 ||
                        !isFinite(coord[0]) || !isFinite(coord[1])
                    );
                    if (hasInvalidCoords) {
                        invalidFeatures.push({ index, reason: 'MultiPoint has invalid coordinates', feature });
                    }
                }
            } else if (feature.geometry.type === 'LineString') {
                const coords = feature.geometry.coordinates;
                if (!coords || !Array.isArray(coords) || coords.length < 2) {
                    invalidFeatures.push({ index, reason: 'LineString must have at least 2 points', feature });
                } else {
                    const hasInvalidCoords = coords.some(coord =>
                        !coord || !Array.isArray(coord) || coord.length < 2 ||
                        !isFinite(coord[0]) || !isFinite(coord[1])
                    );
                    if (hasInvalidCoords) {
                        invalidFeatures.push({ index, reason: 'LineString has invalid coordinates', feature });
                    }
                }
            } else if (feature.geometry.type === 'MultiLineString') {
                const coords = feature.geometry.coordinates;
                if (!coords || !Array.isArray(coords) || coords.length === 0) {
                    invalidFeatures.push({ index, reason: 'Empty MultiLineString coordinates', feature });
                } else {
                    coords.forEach((line, lineIndex) => {
                        if (!line || !Array.isArray(line) || line.length < 2) {
                            invalidFeatures.push({
                                index,
                                reason: `Line ${lineIndex} must have at least 2 points`,
                                feature
                            });
                        } else {
                            const hasInvalidCoords = line.some(coord =>
                                !coord || !Array.isArray(coord) || coord.length < 2 ||
                                !isFinite(coord[0]) || !isFinite(coord[1])
                            );
                            if (hasInvalidCoords) {
                                invalidFeatures.push({
                                    index,
                                    reason: `Line ${lineIndex} has invalid coordinates`,
                                    feature
                                });
                            }
                        }
                    });
                }
            } else if (feature.geometry.type === 'Polygon') {
                const coords = feature.geometry.coordinates;
                if (!coords || coords.length === 0) {
                    invalidFeatures.push({ index, reason: 'Empty coordinates', feature });
                } else {
                    // Check each ring
                    coords.forEach((ring, ringIndex) => {
                        if (!ring || ring.length < 4) {
                            invalidFeatures.push({
                                index,
                                reason: `Ring ${ringIndex} has < 4 points (${ring?.length || 0})`,
                                feature
                            });
                        } else {
                            // Check if ring is closed
                            const first = ring[0];
                            const last = ring[ring.length - 1];
                            if (first[0] !== last[0] || first[1] !== last[1]) {
                                invalidFeatures.push({
                                    index,
                                    reason: `Ring ${ringIndex} not closed`,
                                    feature
                                });
                            }

                            // Check for NaN or Infinity values
                            const hasInvalidCoords = ring.some(coord =>
                                !isFinite(coord[0]) || !isFinite(coord[1])
                            );
                            if (hasInvalidCoords) {
                                invalidFeatures.push({
                                    index,
                                    reason: `Ring ${ringIndex} has NaN/Infinity coordinates`,
                                    feature
                                });
                            }
                        }
                    });
                }
            } else if (feature.geometry.type === 'MultiPolygon') {
                const coords = feature.geometry.coordinates;
                if (!coords || coords.length === 0) {
                    invalidFeatures.push({ index, reason: 'Empty coordinates', feature });
                } else {
                    coords.forEach((polygon, polyIndex) => {
                        polygon.forEach((ring, ringIndex) => {
                            if (!ring || ring.length < 4) {
                                invalidFeatures.push({
                                    index,
                                    reason: `Polygon ${polyIndex} ring ${ringIndex} has < 4 points`,
                                    feature
                                });
                            }
                        });
                    });
                }
            }
        });

        if (invalidFeatures.length > 0) {
            console.error('Found', invalidFeatures.length, 'features with geometry issues:');
            invalidFeatures.forEach(inv => {
                console.error(`  Feature ${inv.index}: ${inv.reason}`);
                console.error(`    Geometry:`, inv.feature.geometry);
            });

            // Alert user about invalid features
            const message = `Warning: ${invalidFeatures.length} feature(s) have invalid geometry:\n\n` +
                invalidFeatures.map(inv => `Feature ${inv.index}: ${inv.reason}`).join('\n') +
                '\n\nThese features will likely be lost in the export. Fix the geometries before exporting.';
            alert(message);
        }

        console.log(`[EXPORT] Exporting ${cleanedGeoJSON.features.length} features`);

        // Detect geometry types
        const geometryTypes = {};
        cleanedGeoJSON.features.forEach(feature => {
            if (feature.geometry && feature.geometry.type) {
                geometryTypes[feature.geometry.type] = true;
            }
        });

        // Options for shapefile export
        const options = {
            folder: cleanFileName,
            types: geometryTypes, // Explicitly specify geometry types
            outputType: 'blob' // Get blob output
        };

        // Use selected projection if specified, otherwise fall back to metadata
        if (projection) {
            options.prj = getProjectionWKT(projection);
        } else if (metadata && metadata.prj) {
            options.prj = metadata.prj;
        }

        // Use custom writer for LineStrings (bulletproof), shp-write for others
        const hasOnlyLineStrings = Object.keys(geometryTypes).length === 1 && geometryTypes['LineString'];

        let zipBlob;

        if (hasOnlyLineStrings) {
            // Use custom writer for LineStrings (shp-write has bugs with multiple LineStrings)
            const JSZip = (await import('jszip')).default;
            const { writeLineStringShapefile } = await import('./customShapefileWriter.js');

            const zip = new JSZip();
            const folder = zip.folder(cleanFileName);

            const shapefileBuffers = await writeLineStringShapefile(cleanedGeoJSON, cleanFileName);

            folder.file('POLYLINE.shp', new Uint8Array(shapefileBuffers.shp));
            folder.file('POLYLINE.shx', new Uint8Array(shapefileBuffers.shx));
            folder.file('POLYLINE.dbf', new Uint8Array(shapefileBuffers.dbf));

            const prjContent = options.prj || getProjectionWKT('EPSG:4326');
            folder.file('POLYLINE.prj', prjContent);

            zipBlob = await zip.generateAsync({ type: 'blob' });
        } else {
            // Use shp-write for Points and Polygons
            zipBlob = await shpwrite.zip(cleanedGeoJSON, options);
        }

        // Verify export (optional validation)
        try {
            const JSZip = (await import('jszip')).default;
            const zip = await JSZip.loadAsync(zipBlob);
            const shpFiles = Object.keys(zip.files).filter(f => f.endsWith('.shp'));

            let totalFeaturesInShapefiles = 0;
            for (const shpFile of shpFiles) {
                try {
                    const shpData = await zip.files[shpFile].async('arraybuffer');
                    totalFeaturesInShapefiles += readShapefileFeatureCount(shpData);
                } catch (countError) {
                    console.warn(`Could not count features in ${shpFile}`);
                }
            }

            if (totalFeaturesInShapefiles < cleanedGeoJSON.features.length) {
                const missing = cleanedGeoJSON.features.length - totalFeaturesInShapefiles;
                console.error(`Export incomplete: ${missing} features lost`);
                alert(`WARNING: ${missing} features were lost during export!`);
            }
        } catch (zipError) {
            // Validation error, but export may still be valid
        }

        // Create download link
        const url = URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${cleanFileName}.zip`;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        console.log(`Shapefile downloaded: ${cleanFileName}.zip`);

    } catch (error) {
        console.error('Error exporting shapefile:', error);
        throw error;
    }
}
